import React from "react";
import { CreditCard, ExternalLink, Lock, Check, ShieldCheck } from "lucide-react";
import { useUnlock } from "@/pages/Dashboard";
import { usePrice } from "@/lib/price";

const WHOP_FALLBACK = "https://whop.com/checkout/plan_hfsvR7lsOjQds";
const REFUND = "/refund";

export default function Billing() {
  const { me } = useUnlock();
  const paid = Boolean(me?.paid);
  const { price, checkout } = usePrice();
  const whop = checkout || WHOP_FALLBACK;

  return (
    <div data-testid="dash-billing" className="space-y-6 max-w-3xl">
      <header>
        <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFC400] inline-block px-2.5 py-1 mb-3">
          BILLING
        </div>
        <h1 className="font-display text-3xl md:text-4xl leading-[0.95]">
          Payments handled by{" "}
          <span className="bg-[#FFC400] px-2">Whop.</span>
        </h1>
        <p className="font-mono-body text-sm mt-3 text-[#0a0a0a]/75 max-w-2xl">
          JobHacki uses Whop for checkout, receipts, and refunds. We never see or store card details — they live with the payment processor.
        </p>
      </header>

      <section
        data-testid="billing-card"
        className={`border-4 border-[#0a0a0a] shadow-brutal p-6 md:p-8 ${
          paid ? "bg-[#00FF66] text-[#0a0a0a]" : "bg-white text-[#0a0a0a]"
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] flex items-center justify-center">
            <CreditCard size={20} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-xl">
              {paid ? "All-Access · active" : "Free account"}
            </div>
            <div className="font-mono-body text-xs opacity-70">
              {paid ? "Lifetime — no renewal" : "Locked tools require All-Access"}
            </div>
          </div>
        </div>

        {!paid ? (
          <>
            <ul className="space-y-2 mt-5 font-mono-body text-sm">
              {[
                `One-time ${price} — lifetime access`,
                "Unlocks every tool we ship",
                "7-day refund via Whop, no questions",
              ].map((l) => (
                <li key={l} className="flex items-start gap-2">
                  <Check size={14} strokeWidth={3} className="mt-0.5 text-[#E10600]" />
                  {l}
                </li>
              ))}
            </ul>
            <a
              href={whop}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="billing-unlock"
              className="hover-pop mt-6 inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFC400] border-4 border-[#0a0a0a] px-5 py-3 font-accent text-sm shadow-brutal"
            >
              <Lock size={14} strokeWidth={3} /> Unlock for {price} on Whop
              <ExternalLink size={11} strokeWidth={3} />
            </a>
          </>
        ) : (
          <p className="font-mono-body text-sm mt-3">
            You&apos;re all-access for life. Any future tools we ship are included — no resubscribe, no upsell wall.
          </p>
        )}
      </section>

      <section className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6 flex items-start gap-3">
        <ShieldCheck size={20} strokeWidth={2.5} className="text-[#E10600] mt-0.5 shrink-0" />
        <div>
          <div className="font-display text-base">Need a refund?</div>
          <p className="font-mono-body text-sm text-[#0a0a0a]/75 mt-1.5 leading-relaxed">
            7-day window, processed through Whop in a click. No surveys, no retention calls. See{" "}
            <a href={REFUND} className="font-bold border-b-2 border-[#0a0a0a]">
              the refund policy
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
